import gql from 'graphql-tag';

import { Mutation, Query, QueryRegistry } from 'core';
import { CONTENT_FIELDS } from 'apps/content/gql';
import { PROFILE_THREAD_CONNECTION_FIELDS } from 'apps/profile/gql';
import { USER_ERROR_FIELDS } from 'core/errors';

const THREAD_LIST_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment threadListFields on Thread {
    id
    membersOnly
    title
    category {
      id
      title
      color
      urlId
    }
    commentsCount
    dateCreated
    lastActivityAt
    creatorProfile {
      id
      username
      fullName
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    urlId
    content {
      ...contentFields
      whitelabel
    }
    forum {
      id
      name
      parentContent {
        id
        slug
        whitelabel
      }
    }
  }
`;

const THREAD_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment threadFields on Thread {
    id
    membersOnly
    canUpdate
    canDelete
    title
    categoryId
    category {
      id
      title
      color
      urlId
    }
    commentsCount
    dateCreated
    lastActivityAt
    creatorProfile {
      id
      username
      fullName
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
    urlId
    content {
      ...contentFields
      whitelabel
    }
  }
`;

export const THREAD_CONNECTION_FIELDS = gql`
  ${THREAD_LIST_FIELDS}
  fragment threadConnectionFields on ThreadConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        ...threadListFields
      }
    }
  }
`;

const CATEGORY_FIELDS = gql`
  fragment categoryFields on Category {
    id
    forumId
    title
    description
    threadsCount
    privateThreadsCount
    urlId
    color
  }
`;

const FORUM_CONNECTION_FIELDS = gql`
  fragment forumConnectionFields on ForumConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        id
        description
        parentContent {
          id
          title
          slug
          avatar {
            id
            path
            url
            filename
            completed
          }
          imageFallbackChar
          content {
            id
            type
            whitelabel
            isFollowed
            followersCount
            creator {
              id
              username
            }
          }
        }
      }
    }
  }
`;

export const FORUM_QUERY = gql`
  ${CATEGORY_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  ${THREAD_FIELDS}
  ${USER_ERROR_FIELDS}
  query Forum(
    $slug: String!
    $threadBatchSize: Int
    $threadCursor: String
    $threadSortBy: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        title
        inSpace {
          id
          whichTypes
        }
        forum {
          id
          description
          isMember
          parentContent {
            id
            whitelabel
            slug
          }
          categories {
            ...categoryFields
          }
          threads(
            first: $threadBatchSize
            after: $threadCursor
            sortBy: $threadSortBy
          ) {
            ...threadConnectionFields
          }
          pinned {
            ...threadFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class ForumQuery extends Query {
  static conf = {
    GQL: FORUM_QUERY,
    varNames: ['slug', 'threadBatchSize', 'threadCursor', 'threadSortBy'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const THREADS_QUERY = gql`
  ${THREAD_CONNECTION_FIELDS}
  query Threads(
    $slug: String
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    initiative(slug: $slug) {
      result {
        id
        forum {
          id
          threads(first: $batchSize, after: $cursor, sortBy: $sortBy) {
            ...threadConnectionFields
          }
        }
      }
    }
  }
`;

export class ThreadsQuery extends Query {
  static conf = {
    GQL: THREADS_QUERY,
    varNames: ['slug', 'batchSize', 'cursor', 'sortBy'],
    resultPath: 'initiative.result.forum.threads',
  };
}

const ALL_THREADS_QUERY = gql`
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  query Threads($batchSize: Int, $cursor: String, $sortBy: String) {
    threads(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      result {
        ...profileThreadConnectionFields
      }
    }
  }
`;

export class AllThreadsQuery extends Query {
  static conf = {
    GQL: ALL_THREADS_QUERY,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'threads.result',
  };
}

const PROFILE_THREADS_QUERY = gql`
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  query ProfileThreads(
    $username: String!
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    profile(username: $username) {
      result(username: $username) {
        id
        threads(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...threadConnectionFields
        }
      }
    }
  }
`;

export class ProfileThreadsQuery extends Query {
  static conf = {
    GQL: PROFILE_THREADS_QUERY,
    varNames: ['username', 'batchSize', 'cursor', 'sortBy'],
    resultPath: 'profile.result.threads',
  };
}

// TODO - comments
const THREAD_QUERY = gql`
  ${THREAD_FIELDS}
  query Thread($urlId: String!) {
    thread(urlId: $urlId) {
      result(urlId: $urlId) {
        ...threadFields
        msg
        isPinned
        canPin
      }
    }
  }
`;

export class ThreadQuery extends Query {
  static conf = {
    GQL: THREAD_QUERY,
    varNames: ['urlId'],
    resultPath: 'thread.result',
  };
}

const THREAD_MUTATION = gql`
  ${THREAD_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation Thread($threadInput: ThreadInput) {
    thread(threadData: $threadInput) {
      thread {
        ...threadFields
        msg
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ThreadMutation extends Mutation {
  static conf = {
    GQL: THREAD_MUTATION,
    inputName: 'threadInput',
    resultPath: 'thread.thread',
    errorPath: 'thread.userErrors',
  };
}

const CATEGORY_QUERY = gql`
  ${USER_ERROR_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  ${THREAD_FIELDS}
  query Category(
    $urlId: String!
    $threadBatchSize: Int
    $threadCursor: String
    $threadSortBy: String
  ) {
    forumCategory(urlId: $urlId) {
      result(urlId: $urlId) {
        id
        urlId
        title
        description
        color
        forum {
          id
          isMember
          description
        }
        threadsCount
        threads(
          first: $threadBatchSize
          after: $threadCursor
          sortBy: $threadSortBy
        ) {
          ...threadConnectionFields
        }
        pinned {
          ...threadFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CategoryQuery extends Query {
  static conf = {
    GQL: CATEGORY_QUERY,
    varNames: ['urlId', 'threadBatchSize', 'threadCursor', 'threadSortBy'],
    resultPath: 'forumCategory.result',
  };
}

const CATEGORIES_QUERY = gql`
  ${CATEGORY_FIELDS}
  ${USER_ERROR_FIELDS}
  query Category($slug: String!) {
    initiative(slug: $slug) {
      result {
        id
        forum {
          id
          categories {
            ...categoryFields
          }
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CategoriesQuery extends Query {
  static conf = {
    GQL: CATEGORIES_QUERY,
    varNames: ['slug'],
    resultPath: 'initiative.result.forum.categories',
  };
}
QueryRegistry.add('forum.CategoriesQuery', CategoriesQuery);

const CATEGORY_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation Category($categoryInput: CategoryInput) {
    category(categoryData: $categoryInput) {
      category {
        id
        title
        description
        color
        urlId
        forum {
          id
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CategoryMutation extends Mutation {
  static conf = {
    GQL: CATEGORY_MUTATION,
    inputName: 'categoryInput',
    resultPath: 'category.category',
    errorPath: 'category.userErrors',
  };
}

const CATEGORY_THREADS_QUERY = gql`
  ${USER_ERROR_FIELDS}
  ${THREAD_CONNECTION_FIELDS}
  query Category(
    $urlId: String!
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    forumCategory(urlId: $urlId) {
      result(urlId: $urlId) {
        id
        threads(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...threadConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CategoryThreadsQuery extends Query {
  static conf = {
    GQL: CATEGORY_THREADS_QUERY,
    varNames: ['urlId', 'batchSize', 'cursor', 'sortBy'],
    resultPath: 'forumCategory.result.threads',
  };
}

const FORUM_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation Forum($forumInput: ForumInput) {
    forum(forumData: $forumInput) {
      forum {
        id
        description
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ForumMutation extends Mutation {
  static conf = {
    GQL: FORUM_MUTATION,
    inputName: 'forumInput',
    resultPath: 'forum.forum',
    errorPath: 'forum.userErrors',
  };
}

const PIN_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation Pin($pinInput: PinInput!) {
    pin(pinData: $pinInput) {
      thread {
        id
        isPinned
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class PinMutation extends Mutation {
  static conf = {
    GQL: PIN_MUTATION,
    inputName: 'pinInput',
    resultPath: 'pin.thread',
    errorPath: 'pin.userErrors',
  };
}

const FORUMS_QUERY = gql`
  query Threads($batchSize: Int, $cursor: String, $sortBy: String) {
    forums {
      result {
        forums(first: $batchSize, after: $cursor, sortBy: $sortBy) {
          ...threadConnectionFields
        }
      }
    }
  }
`;

export class ForumsQuery extends Query {
  static conf = {
    GQL: FORUMS_QUERY,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'forums.result',
    errorPath: 'forums.userErrors',
  };
}

const FEATURED_FORUMS_QUERY = gql`
  ${FORUM_CONNECTION_FIELDS}
  query Threads(
    $contains: [String!]
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    forums(
      contains: $contains
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
    ) {
      result {
        ...forumConnectionFields
      }
    }
  }
`;

export class FeaturedForumsQuery extends Query {
  static conf = {
    GQL: FEATURED_FORUMS_QUERY,
    varNames: ['contains', 'batchSize', 'cursor', 'sortBy'],
    resultPath: 'forums.result',
    errorPath: 'forums.userErrors',
  };
}
